import DynamicDialog from '@/components/DynamicDialog';
import UserDialog from '@/components/UserDialog';
import { userAtom } from '@/global-store';
import useLogoutFn from '@/hooks/useLogoutFn';
import ChangePasswordForm from '@/pages/authentication/auth-forms/ChangePasswordForm';
import { Pencil, KeyRound, LogOut, User, Copy, Check } from 'lucide-react';
import { useAtom } from 'jotai';
import { Fragment, useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography,
  useTailwindBreakpoint,
} from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';

function ProfileSection() {
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const logoutFn = useLogoutFn();
  const isDesktop = useTailwindBreakpoint('xl');

  const [copied, setCopied] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleCopyApiKey = async () => {
    if (!user?.apiKey) return;
    await navigator.clipboard.writeText(user.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const userInfo = {
    name: {
      label: <T k={'name'} />,
      value: user?.firstName + ' ' + user?.lastName,
    },
    email: { label: <T k={'email'} />, value: user?.email },
    phone: { label: <T k={'phone'} />, value: user?.phone },
    company: { label: <T k={'company'} />, value: user?.company },
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size={isDesktop ? 'default' : 'icon'}
          >
            <User aria-hidden />
            <span className='eye:max-xl:sr-only'>{t('profile')}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // lower z-index to be below other modals from this popover
          className='eye:z-1300 eye:p-s! eye:pt-m eye:max-w-[400px] eye:max-h-(--radix-popover-content-available-height)'
          align={'end'}
          matchTriggerWidth={false}
          collisionPadding={16}
        >
          <Typography tag='h4'>{t('profileHeader')}</Typography>
          <div className='eye:mt-2xs eye:mb-s eye:flex eye:flex-col eye:gap-3xs'>
            {Object.entries(userInfo).map(([key, { label, value }]) => (
              <div key={key}>
                <Typography variant='body-s-highlight'>{label}</Typography>
                <Typography className='eye:break-all'>
                  {value || ' - '}
                </Typography>
              </div>
            ))}
          </div>
          <div className='eye:mb-s'>
            <Typography variant='body-s-highlight'>{t('key')}</Typography>
            <div className='eye:flex eye:items-center eye:gap-2xs'>
              <Typography className='eye:break-all eye:flex-1'>
                {'•'.repeat(24)}
              </Typography>
              <button
                type='button'
                onClick={() => void handleCopyApiKey()}
                aria-label={t('copy')}
                disabled={!navigator.clipboard}
                className='eye:shrink-0 eye:cursor-pointer eye:rounded eye:p-3xs eye:text-current eye:transition-colors eye:hover:bg-black/8 eye:disabled:cursor-not-allowed eye:disabled:opacity-40'
              >
                {copied ? (
                  <Check size={16} aria-hidden />
                ) : (
                  <Copy size={16} aria-hidden />
                )}
              </button>
            </div>
          </div>
          <div className='eye:flex eye:flex-col eye:gap-2xs'>
            <UserDialog
              hideOrgAdminToggle
              title={t('editProfile')}
              userData={user}
              useUiButton
              activatorProps={{
                className: 'eye:w-full',
                variant: 'outline',
              }}
              activatorText={
                <Fragment>
                  <Pencil aria-hidden />
                  {t('editProfile')}
                </Fragment>
              }
              maxWidth='md'
            />

            <Button
              type='button'
              variant='outline'
              className='eye:w-full'
              onClick={() => setChangePasswordOpen(true)}
            >
              <KeyRound aria-hidden />
              {t('changePassword')}
            </Button>

            <Button
              type='button'
              onClick={() => void logoutFn()}
              variant='outline'
            >
              <LogOut aria-hidden />
              {t('logOutSB')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <DynamicDialog
        title={t('changePassword')}
        hideActivator
        overwriteOpen={changePasswordOpen}
        onCancel={() => {
          setChangePasswordOpen(false);
          return true;
        }}
        hideActions
      >
        {({ handleCancel }: { handleCancel: () => void }) => (
          <div className='eye:p-3xs'>
            <ChangePasswordForm onCancel={handleCancel} />
          </div>
        )}
      </DynamicDialog>
    </>
  );
}

export default ProfileSection;
